<template>
  <nav id="main-nav" class="navbar navbar-expand-lg navbar-light">
    <div class="container" :class="[{ loggedOut: !admin && !currentUser }]">
      <button
        class="navbar-toggler"
        type="button"
        data-toggle="collapse"
        data-target="#navbarTogglerDemo03"
        aria-controls="navbarTogglerDemo03"
        aria-expanded="false"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon"></span>
      </button>

      <div
        v-if="!admin && currentUser"
        class="collapse navbar-collapse"
        id="navbarTogglerDemo03"
      >
        <ul class="navbar-nav mt-2 mt-lg-0">
          <li
            class="nav-item"
            v-for="item in orderedList"
            :key="item.name"
          >
            <div v-if="item.header === true && item.children.length <= 0">
              <a
                v-if="item.target == 1"
                class="nav-link"
                :class="{ 'active-nav': isItemActive(item) }"
                :href="navHref(item)"
                :data-tour-flow="tourFlowFor(item)"
                @click="onNavClick($event, item)"
              >
                {{ navLabel(item) }}
              </a>

              <a
                v-else
                class="nav-link"
                :class="{ 'active-nav': isItemActive(item) }"
                :href="navHref(item)"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ navLabel(item) }}
              </a>
            </div>

            <div v-else-if="item.header === true && item.children.length > 0">
              <a
                class="nav-link dropdown-toggle"
                :class="{ 'active-nav': isDropdownActive(item) }"
                data-toggle="dropdown"
                href="#"
                role="button"
                aria-haspopup="true"
                aria-expanded="false"
                @click.prevent
              >
                <span>{{ navLabel(item) }}</span>
              </a>

              <div class="dropdown-menu">
                <!-- Children go through visibleNavItems too. They previously honoured NEITHER the
                     Active flag NOR the role CSV, so a scoped child link showed to everyone. -->
                <a
                  v-for="child in visibleNavItems(item.children)"
                  :key="child.name"
                  class="dropdown-item"
                  :class="{ 'active-nav': isActiveNav(child.url) }"
                  :href="child.url"
                >
                  {{ child.name }}
                </a>
              </div>
            </div>

            <div v-else></div>
          </li>
          </ul>
      </div>

      <div
        v-else-if="!admin && !currentUser"
        class="collapse navbar-collapse"
        id="navbarTogglerDemo03"
      >
        <ul class="navbar-nav mr-auto mt-2 mt-lg-0">
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ 'active-nav': isActiveNav('/login') }"
              href="/login"
            >
              Login
            </a>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapGetters } from "vuex";
import APIService from "@/assets/js/APIService";
import _ from "lodash";

const apiService = new APIService();

export default {
  name: "MainNav",

  data() {
    return {
      collapsed: false,
      headerItems: [],
      // Track the mobile breakpoint so labels can be shortened to fit the
      // squeezed equal-column nav on phones without affecting the desktop menu.
      navIsMobile:
        typeof window !== "undefined" && window.innerWidth < 992,
    };
  },

  computed: {
    // effectiveRoleName is kept (other computeds here still read it); effectiveRoleNames is the
    // full role SET the visibility filter needs.
    ...mapGetters({
      currentUser: "currentUser",
      effectiveRoleName: "effectiveRoleName",
      effectiveRoleNames: "effectiveRoleNames",
    }),

    // Reactive admin-route detection — see Head.vue's
    // computed admin() for full rationale. MainNav.vue
    // persists across route changes (lives outside
    // <router-view>), so a data-field assignment in
    // mounted() doesn't update when the user navigates
    // between admin and customer routes via router-link.
    admin() {
      return !!(
        this.$route &&
        this.$route.matched &&
        this.$route.matched.length > 0 &&
        this.$route.matched[0].props &&
        this.$route.matched[0].props.default &&
        this.$route.matched[0].props.default.admin
      );
    },

    // Returns the visibility PREDICATE (a computed returning a function, so it recomputes when the
    // role set changes but can also be applied to submenu children, which are not a computed of
    // their own). One implementation for top-level links and children alike.
    navPasses() {
      // The DB `Active` flag is the single source of truth for visibility:
      // inactive items never appear in the menu (a missing flag is treated as
      // visible so legacy rows still show). Admins toggle Active and reorder in
      // the Account Setup -> Menu Navigation panel.
      // Role visibility (set in Account Setup → Menu Navigation):
      //   `roles`     = CSV of roles ALLOWED to see the link. Empty/missing = everyone.
      //   `denyRoles` = CSV of roles that must NOT see it. Evaluated after, and WINS.
      // Matching is exact per role name ("Sales" ≠ "Sales All") but tested against EVERY role the
      // user holds — Report Access provisions additively, so Sales + Manufacturing + Foundry is a
      // normal shape. The old code compared ONE collapsed role name, which hid role-scoped links
      // from multi-role people depending on which role happened to survive the collapse in auth.js.
      // Respects both admin previews via effectiveRoleNames.
      const mine = Object.create(null);
      for (const r of this.effectiveRoleNames || []) {
        const k = String(r || "").trim().toLowerCase();
        if (k) mine[k] = true;
      }
      // Master-ness comes from currentUser.admin, NOT from mine["master"]. currentUser is the app's
      // own preview-aware admin notion — false while previewing a non-Master role, true again when
      // the preview ends — so the nav can never disagree with the rest of the UI. Deriving it from
      // the role SET breaks "view as user": previewing someone who happens to hold Master (e.g. a
      // Sales+Master+Sales All+Customer account) sets viewAsRole to their PRIMARY role, which strips
      // admin everywhere else, yet their full role array still contains Master — so the nav alone
      // would unlock every link while the rest of the app sat in Sales-shaped preview.
      const isMaster = !!(this.currentUser && this.currentUser.admin);
      const csv = (v) =>
        (v || "")
          .toString()
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      const holdsAny = (list) => list.some((r) => mine[r.toLowerCase()]);

      return (item) => {
        if (!item) return false;
        if (item.active === false || item.active === 0) return false;
        // 1. Master always sees everything — deny never applies to Master.
        if (isMaster) return true;
        // 2. DENY WINS — the entire point of denyRoles: roles='Sales' + denyRoles='Manufacturing'
        //    shows the link to Sales, but hides it from a Sales user who ALSO holds Manufacturing.
        const denied = csv(item.denyRoles);
        if (denied.length && holdsAny(denied)) return false;
        // 3. Empty allow-list still means "everyone" — nearly every existing row relies on this.
        const allowed = csv(item.roles);
        if (!allowed.length) return true;
        // 4. Otherwise visible if the user holds ANY allowed role.
        return holdsAny(allowed);
      };
    },

    orderedList() {
      return _.orderBy((this.headerItems || []).filter(this.navPasses), "sortOrder");
    },
  },

  mounted() {
    this.getUserHeaderNavigation();
    // Re-fetch when an admin edits the menu in the Account Setup panel so
    // show/hide (Active), rename, reorder, add, and delete take effect
    // without a page reload.
    this.$root.$on("nav-updated", this.getUserHeaderNavigation);
    window.addEventListener("resize", this.onNavResize);
  },

  beforeDestroy() {
    this.$root.$off("nav-updated", this.getUserHeaderNavigation);
    window.removeEventListener("resize", this.onNavResize);
  },

  methods: {
    // Apply the SAME visibility rule to any list — used for submenu children, which render straight
    // from item.children and so are not a computed of their own. Before this they honoured NEITHER
    // the Active flag NOR the role CSV, so a scoped child link showed to everyone.
    visibleNavItems(list) {
      if (!Array.isArray(list) || !list.length) return [];
      return _.orderBy(list.filter(this.navPasses), "sortOrder");
    },

    onNavResize() {
      this.navIsMobile = window.innerWidth < 992;
    },

    // Mobile-only label shortening for the squeezed equal-column nav. Labels
    // are DB-driven (Navigation table) and shared with desktop, so we map a
    // few long names to compact forms HERE rather than changing the data —
    // desktop keeps the full label. Match is case-insensitive on the trimmed
    // name; unknown labels pass through unchanged.
    navLabel(item) {
      var name = (item && item.name) || "";
      if (!this.navIsMobile) return name;
      var short = { documents: "Docs" };
      var key = name.toString().trim().toLowerCase();
      return Object.prototype.hasOwnProperty.call(short, key)
        ? short[key]
        : name;
    },

    getUserHeaderNavigation() {
      apiService.getUserHeaderNavigation().then((data) => {
        this.headerItems = data || [];
      });
    },

    // The ?intent on the CURRENT page URL (set by an intent link below).
    // "order" | "quote" | "".
    currentSearchIntent() {
      try {
        return (
          new URLSearchParams(window.location.search).get("intent") || ""
        ).toLowerCase();
      } catch (e) {
        return "";
      }
    },

    // A nav item can carry an "intent" in its `tag` ("order" | "quote") so an
    // admin can point it at the Search page while keeping it highlighted and
    // locking the Search modal's destination. Append the intent as a query so
    // the Search page (and the highlight test below) can read it.
    navHref(item) {
      var tag = ((item && item.tag) || "").toString().toLowerCase();
      if ((tag === "order" || tag === "quote") && item.url) {
        var sep = item.url.indexOf("?") >= 0 ? "&" : "?";
        return item.url + sep + "intent=" + tag;
      }
      return item ? item.url : "#";
    },

    // The guide-tour FLOW key for a nav item — lets the player hand off to that
    // page's live tour when one is mid-run (it reads data-tour-flow). order/quote come
    // from the tag; Search is the 'search' flow; other pages key by route name (the
    // guide builder keys page tours by $route.name). Empty string = no flow.
    tourFlowFor(item) {
      var tag = ((item && item.tag) || "").toString().toLowerCase();
      if (tag === "order" || tag === "quote") return tag;
      var path = this.normalizePath(item && item.url);
      if (path === "/search" || path.indexOf("/search") === 0) return "search";
      try {
        var r = (this.$router && item && item.url) ? this.$router.resolve(item.url) : null;
        return (r && r.route && r.route.name) ? String(r.route.name) : "";
      } catch (e) { return ""; }
    },

    // New Order / New Quote / Search all land on the Search page. Clicking ANY of
    // them must start the Search page FRESH: clear the remembered machine (order +
    // quote side) AND the header search field. Otherwise the page reopens on a stale
    // restored machine (which left Step 1 collapsed/compressed) or a leftover header
    // part-search. These are plain <a href> FULL-RELOAD links, so clearing
    // localStorage synchronously here — before the browser navigates — means the
    // reloaded Search page AND Head.vue both read the cleared state (no preventDefault;
    // the navigation proceeds normally right after).
    onNavClick(ev, item) {
      var tag = ((item && item.tag) || "").toString().toLowerCase();
      var path = this.normalizePath(item && item.url);
      var toSearch = path === "/search" || path.indexOf("/search") === 0;
      if (!toSearch && tag !== "order" && tag !== "quote") return;   // not New Order / New Quote / Search
      // Start the Search page FRESH: drop the remembered machine (order + quote side)
      // and the header search field, so it always opens on "Find Your Machine".
      try { delete localStorage.machineId; } catch (e) {}          // order-side machine
      try { delete localStorage.machineIdRFQ; } catch (e) {}       // quote-side machine
      try { delete localStorage.assemblyId; } catch (e) {}         // order-side assembly
      try { delete localStorage.assemblyIdRFQ; } catch (e) {}      // quote-side assembly
      // THE one that actually rebuilds machine+assembly+part on load: the Search page's
      // applyHeaderSearchQuery() re-runs this stored header search ({code,machineName,
      // assembly}) on mount when the URL has no params. Without clearing it, the page
      // restores the full prior selection no matter what else we wipe.
      try { delete localStorage.headerSearchParams; } catch (e) {}
      try { delete localStorage.searchTerm; } catch (e) {}         // Head.vue search field…
      try { delete localStorage.code; } catch (e) {}               // …+ its part-code/description
      try { delete localStorage.description; } catch (e) {}        //    context
      // NOTE: deliberately NOT touching cartId/cartIdRFQ/companyId/poNumber/configuredWear
      // — those are the cart, company, and display toggle, not the machine selection.
      // During a LIVE guide tour the tour drives navigation itself (SPA, so an in-tab
      // recording isn't killed) — just clear and let it proceed; don't force a reload.
      if (document.querySelector(".guide-tour-widget")) return;
      // Let modified clicks (cmd/ctrl/shift/middle = open in a new tab) behave normally.
      if (ev && (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button === 1)) return;
      // Force a fresh load. A plain <a href> click to the SAME url (e.g. SEARCH while
      // already on /search) is a no-op in the browser/SPA, so the page never reloads
      // and the old machine stays in memory — exactly the "selections are still there"
      // bug. preventDefault + an explicit reload/assign guarantees the reset.
      if (ev && ev.preventDefault) ev.preventDefault();
      var dest = this.navHref(item);
      try {
        var url = new URL(dest, window.location.origin);
        if (url.href === window.location.href) window.location.reload();
        else window.location.assign(url.href);
      } catch (e) {
        window.location.href = dest;
      }
    },

    // Highlight logic that understands intent links:
    //  - An intent item (tag order/quote) is active only when we're on its
    //    target page AND the URL's ?intent matches its tag.
    //  - A plain link to that same page (e.g. bare "Search") stays UNhighlighted
    //    while an intent is active, so the intent item owns the highlight.
    //  - Everything else uses the normal path matching.
    isItemActive(item) {
      if (!item) return false;
      var tag = (item.tag || "").toString().toLowerCase();
      var intent = this.currentSearchIntent();
      var samePath =
        this.normalizePath(item.url) ===
        this.normalizePath(window.location.pathname);
      if (tag === "order" || tag === "quote") {
        return samePath && intent === tag;
      }
      if (intent && samePath) return false;
      return this.isActiveNav(item.url);
    },

    normalizePath(url) {
      if (!url) return "";

      try {
        const parsedUrl = new URL(url, window.location.origin);
        let path = parsedUrl.pathname || "/";

        path = path.toLowerCase();

        if (path.length > 1 && path.endsWith("/")) {
          path = path.slice(0, -1);
        }

        return path;
      } catch (error) {
        let path = String(url).toLowerCase();

        if (!path.startsWith("/")) {
          path = `/${path}`;
        }

        if (path.length > 1 && path.endsWith("/")) {
          path = path.slice(0, -1);
        }

        return path;
      }
    },

    isActiveNav(url) {
      if (!url) return false;

      const currentPath = this.normalizePath(window.location.pathname);
      const targetPath = this.normalizePath(url);

      if (!targetPath) return false;

      if (targetPath === "/") {
        return currentPath === "/";
      }

      // Same exact-match treatment for /account — without
      // this, the prefix rule below would activate HOME for
      // every /account/* subpage (savedcarts, orderdetails,
      // information, etc.), which then races with our path-
      // alias logic and HOME wins because it's earlier in
      // the menu order. HOME should only highlight when the
      // user is actually on the Account Dashboard.
      if (targetPath === "/account") {
        return currentPath === "/account";
      }

      // Standard match: exact path or prefix match (e.g.
      // /account/orderstatus matches /account/orderstatus/12).
      if (
        currentPath === targetPath ||
        currentPath.startsWith(`${targetPath}/`)
      ) {
        return true;
      }

      // ===== PATH ALIASES =====
      // Some routes don't have their own top-nav entry but
      // logically belong under one. Alias them here so the
      // right top-nav button highlights even though the URL
      // doesn't match the nav item's primary path.
      //
      // SavedCarts hosts three tabs (Saved / Orders / Quotes)
      // and serves as the consolidated landing for what used
      // to be /account/orderhistory and /account/rfqhistory.
      // The detail drilldown pages (/account/orderdetails/:id
      // and /account/rfqdetails/:id) are reached from those
      // tabs and should keep the same nav highlighted.
      //
      // All four paths alias to whichever nav item points at
      // /account/orderhistory (currently labeled "Order Status"
      // in the database — the URL field still uses the old
      // "orderhistory" path even though the link is now a
      // consolidated landing for Saved/Orders/Quotes tabs and
      // is labeled Order Status). If admins later rename the
      // link's URL, update the targetPath check below to match
      // the new value.
      //
      // TODO: When the broader admin/navigation refactor
      // happens, replace this hardcoded alias list with a
      // MatchPaths (or AdditionalUrls) column on
      // dbo.Navigation so admins can configure which paths
      // activate which nav item without touching code.
      const orderStatusAliases = [
        "/account/savedcarts",
        "/account/orderdetails",
        "/account/rfqdetails",
      ];
      if (targetPath === "/account/orderhistory") {
        return orderStatusAliases.some(function (alias) {
          return (
            currentPath === alias ||
            currentPath.startsWith(`${alias}/`)
          );
        });
      }

      return false;
    },

    isDropdownActive(item) {
      if (!item || !item.children || !item.children.length) {
        return false;
      }

      return item.children.some((child) => this.isActiveNav(child.url));
    },
  },
};
</script>

<style scoped>
/* =============================================================
   MAIN NAV — PERFORATED STEEL BACKDROP
   =============================================================
   The nav strip uses the perforated-steel texture as its
   background so the navigation reads as part of the industrial
   / Steele visual identity rather than a generic Bootstrap nav.

   ASSET LOCATION
   ---------------------------------------------------------------
   File lives at src/assets/img/Perforated_Steel.jpg (matching
   the original upload's filename — capital P, capital S,
   underscore between words). If the file in your repo uses a
   different name, edit the url() string below to match — the
   `~@` prefix is the webpack convention for resolving the
   `@` alias inside a CSS url() value.

   APPROACH
   ---------------------------------------------------------------
   No dark gradient overlay — the user wants the steel grain
   visible and prefers black text against the (lighter) parts
   of the texture rather than white text against a darkened
   wash. Active state stays red + white so it still pops against
   the steel; hover gets a subtle dark wash so the tab still
   provides interaction feedback without inverting the color
   scheme on every mouseover.

   The background-size is `cover` so a single copy of the
   image scales to fill the entire nav bar — no tiling.
   Tiling produced visible seams every 220px because each
   tile carried the vignetted dark edges of the source
   image, and adjacent tiles butted those dark edges
   together. `cover` + `no-repeat` reads as one continuous
   sheet of perforated steel from end to end.
   ============================================================= */
#main-nav.navbar {
  background-image: url("~@/assets/img/Perforated_Steel.jpg") !important;
  /* Tile a SMALLER copy of the (now vignette-cropped) source
     image so perforations read as fine, dense steel rather
     than a few oversized holes. The source has been cropped
     to remove its outer 250px on all sides — the original
     had heavy vignetting in the corners that produced visible
     seams when tiled. With the cropped (uniform-brightness)
     image, a 380px tile blends invisibly across the nav.

  /* Tile size pinned to 220px wide × full nav height.
     Smaller than the previous 380px → perforations render
     finer and denser, reading more like a real perforated
     metal sheet at this zoom level. The vignette-cropped
     source image still tiles seamlessly at this size since
     brightness is uniform end-to-end. */
  background-size: 220px 100% !important;
  background-position: center center !important;
  background-repeat: repeat-x !important;
  /* Light fallback color in case the image fails to load —
     prevents the bar from collapsing to Bootstrap's default
     white if the asset path is wrong on a future move. */
  background-color: #b0b0b0 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.25);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

/* Keep the DESKTOP nav a single, stable-height horizontal row. Measured via the
   debug overlay: toggling dev tools (crossing the lg breakpoint mid-reflow)
   left #main-nav stuck at h=69px instead of its natural h=42px — the steel band
   "heightened" because its background is background-size:220px 100% (fills 100%
   of the nav height). Cause: Bootstrap's responsive collapse can leave the
   .navbar-collapse with a stale inline height, or the tabs wrapped to a 2nd
   line. Forcing the expanded layout + nowrap + auto height at >=992px overrides
   any stale collapse state so the band can't grow. Below 992px the normal
   hamburger collapse is left untouched. */
@media (min-width: 992px) {
  /* The navbar's OWN flex container must not wrap the toggler/collapse to a
     2nd row, and the toggler must stay hidden (a stale-visible toggler would
     add a row). align-items:center keeps a tall child from stretching the bar. */
  #main-nav.navbar {
    flex-wrap: nowrap !important;
    align-items: center !important;
  }
  #main-nav .navbar-toggler {
    display: none !important;
  }
  #main-nav .navbar-collapse {
    display: flex !important;
    flex-basis: auto !important;
    height: auto !important;
    overflow: visible !important;
  }
  #main-nav .navbar-nav {
    flex-direction: row !important;
    flex-wrap: nowrap !important;
  }
  /* The nav .container is display:flex; a clearfix ::before/::after (both show
     display:block in the overlay) acts as an extra flex item, and if the
     container ever stacks (flex-direction:column) that pseudo's text-line
     (~26px) stacks ON TOP of the 41px menu -> the 67px band. Force a single
     row and kill the pseudos so the container can only ever be the menu's
     height. */
  #main-nav > .container {
    flex-direction: row !important;
  }
  #main-nav > .container::before,
  #main-nav > .container::after {
    display: none !important;
    content: none !important;
  }
}

/* ===== NAV LINKS — DEFAULT STATE =====
   Black text per the user's spec. The slight white text-shadow
   bumps contrast against any darker patches of the steel grain
   without being visually obvious. Font weight 700 keeps the
   letters thick enough to remain crisp over the busy
   perforation pattern.

   Square corners (border-radius: 0) — the default Bootstrap
   nav-link rounding read as "buttons" rather than "tabs"; the
   user wants flat tab-style edges so the nav reads as a single
   continuous strip.

   Dark gray right-border serves as the separator between
   adjacent tabs. The :last-child rule below clears it on the
   final tab so the right edge stays clean. Color is dark
   enough to read clearly against the medium-toned steel
   without competing with the active-state red. */
#main-nav .nav-link {
  color: #000 !important;
  text-shadow: 0 1px 1px rgba(255, 255, 255, 0.5);
  font-weight: 700;
  letter-spacing: 0.02em;
  transition: background-color 0.15s ease, color 0.15s ease;
  border-radius: 0 !important;
  border-right: 1px solid #3a3a3a;
}
/* Drop the trailing separator so the rightmost tab doesn't
   end with a hanging vertical line. Selector reaches through
   the wrapper <div> the template adds inside each <li>. */
#main-nav .navbar-nav > .nav-item:last-child .nav-link {
  border-right: none;
}

/* ===== HOVER STATE =====
   Red background + white text on hover — same color
   palette as the active state, so hovering a non-active
   tab gives the user a clear preview of what "selecting"
   that tab will look like. Reverts when the cursor leaves.
   text-shadow cleared because the dark shadow we use to
   help black text read on the steel grain would interfere
   with the high-contrast white-on-red treatment. */
#main-nav .nav-link:hover,
#main-nav .nav-link:focus {
  background-color: #c0392b;
  color: #fff !important;
  text-shadow: none;
}

/* ===== ACTIVE STATE =====
   The existing .active-nav class is what the template applies
   to the current-route tab. Red background, white text — the
   red/silver pairing matches the JC Steele logo and pops
   loudly against the steel without competing with it. */
#main-nav .nav-link.active-nav,
#main-nav .nav-link.active-nav:hover,
#main-nav .nav-link.active-nav:focus {
  background-color: #c0392b !important;
  color: #fff !important;
  text-shadow: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
}

/* ===== DROPDOWN TOGGLE / MENU =====
   The trigger inherits black-text + white-shadow styling from
   .nav-link. The menu panel itself stays light (default
   Bootstrap dropdown) so it reads cleanly against the page
   content below — only the trigger is themed for the steel. */
#main-nav .dropdown-menu {
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.18);
}
#main-nav .dropdown-item {
  color: #2a2a2a;
}
#main-nav .dropdown-item:hover,
#main-nav .dropdown-item:focus {
  background-color: #f4f4f4;
  color: #2a2a2a;
}
#main-nav .dropdown-item.active-nav {
  background-color: #c0392b;
  color: #fff;
}

/* ===== MOBILE TOGGLER =====
   Hamburger icon needs to be visible against the steel. With
   black text overall, a dark-stroked icon SVG matches the rest
   of the nav text. Border tones down to gray-on-steel. */
#main-nav .navbar-toggler {
  border-color: rgba(0, 0, 0, 0.4);
}
#main-nav .navbar-toggler-icon {
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg viewBox='0 0 30 30' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke='rgba(0,0,0,0.85)' stroke-width='2' stroke-linecap='round' stroke-miterlimit='10' d='M4 7h22M4 15h22M4 23h22'/%3E%3C/svg%3E");
}

/* ===== MOBILE: inline dark menu bar (no hamburger, no steel) =====
   Mobile redesign: drop the perforated-steel texture for a plain dark bar,
   hide the hamburger, and show the menu items inline (Home · Order · …),
   horizontal-scrolling if they overflow a narrow screen. White text replaces
   the black-on-steel. The logo / account no longer reparent into the nav
   (responsive.js); the hide rules are a defensive net for stale builds. */
@media (max-width: 991.98px) {
  #main-nav.navbar {
    /* Flat color (no steel texture) so it can adapt to color modes; light-gray
       in light mode (was a hardcoded near-black bar). Zero padding so the menu
       runs edge-to-edge. */
    background-image: none !important;
    background-color: #92969c !important;
    padding: 0 !important;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.12);
  }
  #main-nav .navbar-toggler {
    display: none !important;
  }
  #main-nav .navbar-collapse {
    display: flex !important;
    flex-basis: auto !important;
    height: auto !important;
    overflow: visible !important;
    width: 100% !important;
  }
  /* Edge-to-edge: kill Bootstrap's .container max-width / padding / auto-margins
     so the first item touches the LEFT edge and the last touches the RIGHT. */
  #main-nav > .container {
    flex-wrap: nowrap !important;
    max-width: 100% !important;
    width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  #main-nav .navbar-nav {
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    width: 100% !important;
    align-items: stretch !important;
    overflow-x: visible !important;   /* every item fits — no scrolling */
  }
  /* EQUAL-WIDTH COLUMNS: every nav item gets the same slice of the bar, so all
     of them fit regardless of how many there are or how long the (DB-driven)
     labels are. Long labels (e.g. ORDER HISTORY) wrap to two centered lines
     instead of overflowing. */
  #main-nav .navbar-nav > li.nav-item {
    flex: 1 1 0 !important;
    min-width: 0 !important;
    display: flex !important;
    align-items: stretch !important;
    border-left: 1px solid rgba(0, 0, 0, 0.16) !important;
  }
  #main-nav .navbar-nav > li.nav-item:first-child {
    border-left: none !important;
  }
  #main-nav .navbar-nav > li.nav-item > div {
    display: flex !important;
    width: 100% !important;
    align-items: stretch !important;
  }
  /* The link fills its column, centered, wrapping to 2 lines. !important is
     REQUIRED to beat the desktop rule `#main-nav li.nav-item a {padding:15px
     30px}` (specificity 1,1,2) that was forcing huge padding on mobile. */
  #main-nav .nav-link {
    width: 100% !important;
    /* Black text on the light-gray bar (matches desktop); turns white on the
       red hover/active state below. */
    color: #000 !important;
    text-shadow: 0 1px 1px rgba(255, 255, 255, 0.45) !important;
    border-left: 0 !important;
    border-right: 0 !important;
    white-space: normal !important;
    text-align: center !important;
    font-size: 10px !important;
    line-height: 1.12 !important;
    letter-spacing: 0 !important;
    padding: 5px 3px !important;
    min-height: 42px;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  /* Hover AND current page → brand-red block, white text (matches desktop). */
  #main-nav .nav-link:hover,
  #main-nav .nav-link:focus,
  #main-nav .nav-link.active-nav,
  #main-nav .nav-link.active {
    background: #c0392b !important;
    color: #fff !important;
    text-shadow: none !important;
  }
  /* Accommodate color modes — darker gray bar in mid / dark mode (white text
     stays readable). Light mode uses the #92969c above. */
  html.mid-mode #main-nav.navbar {
    background-color: #6c727a !important;
  }
  html.dark-mode #main-nav.navbar {
    background-color: #3a3f46 !important;
  }
  /* Logo / search / account live in the header now — hide if a stale build
     reparented them into the nav. */
  #main-nav #logo,
  #main-nav form#search,
  #main-nav #header-account {
    display: none !important;
  }
}
</style>